import { useGetBaseInfo } from '@/common/apis/services/config/baseInfo';
import { useSearch as useSearchRequest } from '@/common/apis/services/search/search';
import { Center } from '@/common/types/doctorParams';
import { useSearchRouter } from './useSearchRouter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchStore } from '../store/search';
import { isInPersonView, toInPersonResults } from '../functions/inPersonView';

type Filter = {
  items: {
    count: number;
    title: string;
    value: string;
  }[];
  name: string;
  title: string;
  type: 'radio' | 'switch' | 'slider_with_count';
};

export type Category = {
  title: string;
  sub_categories?: Category[];
  value: string;
  url: string;
  count?: number;
};

type Search = {
  pagination: {
    limit: number;
    page: number;
  };
  result: Result[];
  total: number;
  is_landing: boolean;
  query_id: string;
  semantic_search?: any;
};

export type Result = {
  _id: string;
  id: string;
  position: number;
  count: string;
  badges: Badge[];
  display_address: string;
  display_expertise: string;
  experience: number;
  image: string;
  prefix: string;
  is_bulk: boolean;
  rates_count: number;
  satisfaction: number;
  title: string;
  type: 'doctor' | 'center';
  url: string;
  view: string;
  price: string;
  server_id: number;
  centers: Center[];
  actions: {
    title: string;
    top_title: string;
    outline: boolean;
    url: string;
  }[];
};

export type Badge = {
  icon: `${'star' | 'smile' | 'clock'}-icon`;
  title: string;
  type: 'info' | 'success';
  description?: string;
};

// DR-07 (ghane docs/dev-requests/dr-07-online-visit-suggestion-to-backend.md §13): backend-computed
// sibling key next to `search` — absent entirely when there is no suggestion (Q-1/Q-3/M-5). `banner`
// and `card` are shaped exactly like `search.result[]` items (SearchV1Resource::shapePromotedOnlineVisit).
export type PromotedOnlineVisit = {
  banner: Result;
  card?: Result;
  display_index: number;
  is_promoted: boolean;
};

type SeoInfo = {
  title: string;
  heading: string;
  description: string;
  seo_box: string;
  canonical_link: string;
  jsonld: {}[];
  breadcrumbs: {
    text: string;
    href: string;
  }[];
};

type Footers = {
  title: string;
  items: {
    name: string;
    url: string;
  }[];
}[];

export const useSearch = () => {
  const {
    query: { params, ...query },
    asPath,
  } = useSearchRouter();
  const geoLocation = useSearchStore(state => state.geoLocation);

  const baseInfo = useGetBaseInfo({ table: ['city', 'province'] });
  const searchRequest = useSearchRequest({
    route: (params as string[])?.join('/') ?? '',
    query: {
      ...query,
      ...(geoLocation && {
        ...geoLocation,
      }),
    },
  });

  const {
    search = { result: [], is_landing: false, pagination: { limit: 20, page: 1 }, total: 0, query_id: '' },
    filters = [],
    categories = [],
    order_items: orderItems = {},
    selected_filters: selectedFilters = {},
    seo_info: seoInfo = undefined,
    footers = undefined,
    promoted_online_visit: promotedOnlineVisit = undefined,
  }: {
    search: Search | undefined;
    filters: Filter[];
    categories: Category[];
    order_items: Record<string, string | string[]>;
    selected_filters: Record<string, string | string[]>;
    seo_info: SeoInfo | undefined;
    footers: Footers | undefined;
    promoted_online_visit?: PromotedOnlineVisit;
  } = useMemo(() => {
    return searchRequest.data ?? {};
  }, [searchRequest.data]);

  // DR-07 §13 condition 2: derived synchronously from the same `searchRequest.data` object that
  // `search` above comes from (plain useMemo, no useState/useEffect of its own) — so it lands in the
  // exact same render as `search`/`result` and never lags a tick behind them. `banner`/`card` are
  // mapped onto the old `.search.result[0]`/`.search.result[1]` positions so the untouched Plasmic
  // markup (which still reads `searchConsultResponse.search.result[...]`) keeps rendering byte-identical
  // output. `is_promoted` is stamped onto the card item itself so the generic result-card repeater can
  // branch its click analytics off `currentItem.is_promoted` without any new prop plumbing.
  // «نوبت حضوری» filter: no online-visit offers on the page (functions/inPersonView.ts).
  const inPersonView = isInPersonView(query.turn_type);

  const searchConsultResponseData = useMemo(() => {
    if (!promotedOnlineVisit || inPersonView) return {};
    const items = [
      promotedOnlineVisit.banner,
      promotedOnlineVisit.card ? { ...promotedOnlineVisit.card, is_promoted: promotedOnlineVisit.is_promoted } : undefined,
    ].filter(Boolean);
    return {
      search: { result: items },
      display_index: promotedOnlineVisit.display_index,
    };
  }, [promotedOnlineVisit, inPersonView]);

  const [result, setResult] = useState(search?.result ?? []);
  const [isLoading, setIsLoading] = useState(searchRequest.isLoading);

  // A new URL means new results are coming. This used to be an effect only, so the first commit for the
  // new path still rendered the PREVIOUS list (a whole extra render of every card — about half of the
  // page's render work on a mid-range phone, docs/performance.md §۳.۱۴) and only then flipped to
  // "loading + empty". Adjusting state while rendering (React's documented pattern) makes that first
  // commit already show loading + empty. The commits after it — and so every effect that depends on
  // `result`/`isLoading` — are the same as before, so analytics events do not change.
  const [seenPath, setSeenPath] = useState(asPath);
  if (seenPath !== asPath) {
    setSeenPath(asPath);
    setIsLoading(true);
    if (!query.page) setResult([]);
  }

  useEffect(() => {
    setIsLoading(true);
    if (!query.page) {
      // Already emptied while rendering; keep the identity so this does not schedule another render.
      setResult(prev => (prev.length === 0 ? prev : []));
    }
  }, [asPath]);

  // Also re-run on asPath: a URL change always starts loading (above), but a URL whose query is
  // already cached (keep-alive search shown again at `/s/?` vs `/s`, Back to a cached search) hands
  // back the *same* data object — keyed on [search] alone, loading never ended.
  // A page > 1 is appended only once per data object, so a re-run can't duplicate it.
  const appliedSearch = useRef<Search | null>(null);
  useEffect(() => {
    if (!searchRequest.isSuccess || searchRequest.isPreviousData) return;
    setIsLoading(false);
    const alreadyApplied = appliedSearch.current === search;
    appliedSearch.current = search;
    if (search?.result.length === 0) {
      return setResult([]);
    }
    if (search?.pagination?.page > 1) {
      if (!alreadyApplied) setResult(prev => [...prev, ...search.result]);
    } else {
      setResult([...search.result]);
    }
  }, [search, asPath, searchRequest.isSuccess, searchRequest.isPreviousData]);

  const isLanding = useMemo(
    () => (!isLoading ? search.is_landing : params?.length === 1 && query.text === undefined),
    [params, query, search.is_landing, isLoading],
  );

  const selectedCategory = useMemo(() => categories?.find(item => item.value === selectedFilters?.category), [selectedFilters]);

  const selectedSubCategory = useMemo(
    () =>
      selectedCategory?.sub_categories?.find(
        item => item.value === `exp-${(selectedFilters?.sub_category as string)?.replace('exp-', '')}`,
      ) ?? undefined,
    [selectedFilters],
  );

  const searchCity = useMemo(
    () => baseInfo.data?.data?.result?.city?.find((item: any) => item.en_slug === selectedFilters.city),
    [baseInfo.data, selectedFilters.city],
  );

  const isConsult = useMemo(() => query.turn_type === 'consult', [query.turn_type]);

  const shownResult = useMemo(() => (inPersonView ? toInPersonResults(result) : result), [result, inPersonView]);

  return {
    isLanding,
    isLoading,
    result: shownResult,
    isSuccess: searchRequest.isSuccess,
    isError: searchRequest.isError,
    error: searchRequest.error,
    refetch: searchRequest.refetch,
    total: search?.total,
    pagination: search?.pagination,
    filters,
    categories,
    selectedFilters,
    orderItems,
    footers,
    seoInfo,
    selectedCategory,
    selectedSubCategory,
    searchCity,
    isConsult,
    isInPersonView: inPersonView,
    search,
    responseData: searchRequest?.data ?? {},
    searchConsultResponseData,
  };
};
